import React, { useState, useEffect } from 'react';
import { apiUrl } from '../api';

const numberToWords = (num) => {
  if (num === 0) return "ZERO";
  const a = ['', 'ONE ', 'TWO ', 'THREE ', 'FOUR ', 'FIVE ', 'SIX ', 'SEVEN ', 'EIGHT ', 'NINE ', 'TEN ', 'ELEVEN ', 'TWELVE ', 'THIRTEEN ', 'FOURTEEN ', 'FIFTEEN ', 'SIXTEEN ', 'SEVENTEEN ', 'EIGHTEEN ', 'NINETEEN '];
  const b = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];
  const inWords = (n) => {
    let str = '';
    if (n > 99) { str += a[Math.floor(n / 100)] + 'HUNDRED '; n %= 100; }
    if (n > 19) { str += b[Math.floor(n / 10)] + ' '; n %= 10; }
    if (n > 0) { str += a[n]; }
    return str.trim();
  };
  let result = '';
  if (num >= 10000000) { result += inWords(Math.floor(num / 10000000)) + ' CRORE '; num %= 10000000; }
  if (num >= 100000) { result += inWords(Math.floor(num / 100000)) + ' LAKH '; num %= 100000; }
  if (num >= 1000) { result += inWords(Math.floor(num / 1000)) + ' THOUSAND '; num %= 1000; }
  if (num > 0) { result += inWords(num); }
  return result.trim() + " ONLY";
};

const SalesReturn = () => {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Product catalog built from previous SALES to allow returning them
  const [salesCatalog, setSalesCatalog] = useState([]);

  useEffect(() => {
    const fetchSalesCatalog = async () => {
      try {
        const response = await fetch(apiUrl('/api/sales'));
        if (response.ok) {
          const sales = await response.json();
          const catalogList = [];
          
          sales.forEach(sale => {
            if (sale.items) {
              sale.items.forEach(item => {
                catalogList.push({
                  name: item.productName,
                  customerName: item.customerName || sale.customerName || 'Unknown Customer',
                  invoiceNo: item.invoiceNo || sale.invoiceNo || '',
                  rate: item.rate || 0,
                  hsnNo: item.hsnNo || '',
                  batch: item.batch || '',
                  originalQty: item.qty || 0
                });
              });
            }
          });
          
          setSalesCatalog(catalogList);
        }
      } catch (error) {
        console.error("Failed to load sales catalog:", error);
      }
    };
    
    fetchSalesCatalog();
  }, []);

  // State for rows
  const [rows, setRows] = useState([
    { id: 1, customerName: '', invoiceNo: '', productName: '', hsnNo: '', batch: '', qty: 1, rate: 0, amount: 0, returnReason: 'Defective' }
  ]);

  const [discount, setDiscount] = useState('');
  const [addAmount, setAddAmount] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const removeRow = (id) => {
    if (rows.length > 1) {
      setRows(rows.filter(r => r.id !== id));
    }
  };

  const updateRow = (id, field, value) => {
    setRows(prevRows => {
      let isLastRowAndRequiresNew = false;
      const newRows = prevRows.map((r, index) => {
        if (r.id === id) {
          const updated = { ...r, [field]: value };
          
          // Auto-fill logic for product name
          if (field === 'productName') {
            const matchedProduct = salesCatalog.find(p => p.name.toLowerCase() === value.toLowerCase());
            if (matchedProduct) {
              updated.rate = matchedProduct.rate;
              updated.hsnNo = matchedProduct.hsnNo || '';
              updated.batch = matchedProduct.batch || '';
              updated.customerName = matchedProduct.customerName || '';
              updated.invoiceNo = matchedProduct.invoiceNo || '';
            }
          }
  
          if (field === 'qty' || field === 'rate' || field === 'productName') {
            updated.amount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          }
          
          // If editing the last row and it's not empty, spawn a new row
          if (index === prevRows.length - 1 && value.toString().trim() !== '' && field !== 'showSuggestions') {
            isLastRowAndRequiresNew = true;
          }
          
          return updated;
        }
        return r;
      });

      if (isLastRowAndRequiresNew) {
        newRows.push({ 
          id: Date.now(), 
          customerName: newRows[newRows.length - 1]?.customerName || '', 
          invoiceNo: newRows[newRows.length - 1]?.invoiceNo || '', 
          productName: '', hsnNo: '', batch: '', qty: 1, rate: 0, amount: 0, returnReason: 'Defective'
        });
      }

      return newRows;
    });
  };

  const validRows = rows.filter(r => r.productName.trim() !== '' && r.amount > 0);
  const grossAmount = validRows.reduce((sum, r) => sum + r.amount, 0);
  const discountValue = parseFloat(discount) || 0;
  const addValue = parseFloat(addAmount) || 0;
  const netAmount = grossAmount - discountValue + addValue;

  const handleSaveReturn = async () => {
    if (validRows.length === 0) {
      alert("Please add at least one product with an amount before saving.");
      return;
    }

    try {
      setIsSaving(true);
      const returnData = {
        date,
        grossAmount,
        discount: discountValue,
        addAmount: addValue,
        netAmount,
        items: validRows.map(r => ({
          customerName: r.customerName,
          invoiceNo: r.invoiceNo,
          productName: r.productName,
          hsnNo: r.hsnNo,
          batch: r.batch,
          qty: parseInt(r.qty) || 0,
          rate: parseFloat(r.rate) || 0,
          amount: r.amount,
          returnReason: r.returnReason
        }))
      };

      const response = await fetch(apiUrl('/api/sales-return'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(returnData)
      });

      if (!response.ok) {
        throw new Error('Failed to save on backend');
      }
      
      alert('Sales Return Saved Successfully to Firestore!');
      
      // Reset form after save
      setRows([{ id: Date.now(), customerName: '', invoiceNo: '', productName: '', hsnNo: '', batch: '', qty: 1, rate: 0, amount: 0, returnReason: 'Defective' }]);
      setDiscount('');
      setAddAmount('');
    } catch (error) {
      console.error("Error saving return: ", error);
      alert('Failed to save return. Make sure your backend server is running.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="animate-in fade-in duration-700 space-y-2 p-1 sm:p-2">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-6 sm:h-8 rounded-full bg-rose-600 shadow-sm"></div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-800 tracking-wide">Sales Return Entry</h2>
          </div>
          
          <div className="flex justify-end">
            <div className="flex items-center gap-2 sm:gap-3 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm">
              <label className="text-[10px] sm:text-xs font-bold text-slate-500 whitespace-nowrap">Date:</label>
              <input 
                type="date" 
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="outline-none text-slate-800 text-[10px] sm:text-xs font-bold bg-transparent cursor-pointer"
              />
            </div>
          </div>
        </div>

      {/* Main Single-Line Data Entry Area */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden flex flex-col min-h-[75vh]">
        <div className="overflow-x-auto w-full flex-grow">
          <table className="w-full text-left border-collapse whitespace-nowrap">
            <thead>
              <tr className="bg-[#1e5c5c] text-white shadow-sm">
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider w-10 text-center">#</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider min-w-[150px]">Customer Name</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider min-w-[100px]">Original Bill No</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider min-w-[200px]">Product Name (Search)</th>
                <th className="py-1.5 px-2 text-[11px] font-black uppercase tracking-wider min-w-[90px]">Batch</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider min-w-[120px]">Reason</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider w-20">R. Qty</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider w-24">Rate (₹)</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider w-28">Refund Amount</th>
                <th className="py-1.5 px-3 text-[11px] font-black uppercase tracking-wider w-10 text-center"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, index) => (
                <tr key={row.id} className="group hover:bg-slate-50/40 transition-colors">
                  <td className="py-1.5 px-3 text-[9px] font-bold text-slate-400 text-center">{index + 1}</td>
                  
                  <td className="py-1.5 px-3">
                    <input 
                      type="text" 
                      value={row.customerName}
                      onChange={(e) => updateRow(row.id, 'customerName', e.target.value)}
                      placeholder="Customer name"
                      className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 text-[9px] font-bold text-slate-800 py-1.5 px-1 outline-none transition-all placeholder:font-black placeholder:text-slate-300"
                    />
                  </td>

                  <td className="py-1.5 px-3">
                    <input 
                      type="text" 
                      value={row.invoiceNo}
                      onChange={(e) => updateRow(row.id, 'invoiceNo', e.target.value)}
                      placeholder="BILL-001"
                      className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 text-[9px] font-bold text-slate-800 py-1.5 px-1 outline-none transition-all uppercase placeholder:font-black placeholder:text-slate-300"
                    />
                  </td>

                  <td className="py-1.5 px-3 relative">
                    <input 
                      type="text" 
                      value={row.productName}
                      onChange={(e) => {
                        updateRow(row.id, 'productName', e.target.value);
                        updateRow(row.id, 'showSuggestions', true);
                      }}
                      onFocus={() => updateRow(row.id, 'showSuggestions', true)}
                      onBlur={() => setTimeout(() => updateRow(row.id, 'showSuggestions', false), 200)}
                      placeholder="Type product name"
                      className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 text-[9px] font-bold text-slate-800 py-1.5 px-1 outline-none transition-all placeholder:font-black placeholder:text-slate-300"
                    />
                    
                    {/* Custom Autocomplete Dropdown */}
                    {row.showSuggestions && (
                      <div className="absolute z-50 left-0 top-full mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                        {salesCatalog.filter(p => p.name.toLowerCase().includes(row.productName.toLowerCase())).length > 0 ? (
                          salesCatalog
                            .filter(p => p.name.toLowerCase().includes(row.productName.toLowerCase()))
                            .map((p, idx) => (
                              <div 
                                key={idx}
                                className="px-4 py-1 hover:bg-blue-50 cursor-pointer border-b border-slate-50 last:border-0 flex justify-between items-center transition-colors"
                                onMouseDown={() => {
                                  updateRow(row.id, 'productName', p.name);
                                  setTimeout(() => updateRow(row.id, 'showSuggestions', false), 50);
                                }}
                              >
                                <div>
                                  <div className="text-[9px] font-bold text-slate-700">{p.name}</div>
                                  <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{p.customerName} {p.invoiceNo ? `(${p.invoiceNo})` : ''}</div>
                                </div>
                                <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">₹{p.rate}</span>
                              </div>
                            ))
                        ) : row.productName ? (
                          <div className="px-4 py-1 text-[9px] font-bold text-slate-400 text-center">No past sales found for this product.</div>
                        ) : (
                          <div className="px-4 py-1 text-[9px] font-bold text-slate-400 text-center">Type to search past sales...</div>
                        )}
                      </div>
                    )}
                  </td>

                  <td className="py-1.5 px-2">
                    <input 
                      type="text" 
                      value={row.batch}
                      onChange={(e) => updateRow(row.id, 'batch', e.target.value)}
                      placeholder="Batch"
                      className="w-full bg-transparent border-b-2 border-slate-200 focus:border-blue-500 text-[9px] font-bold text-slate-800 py-1.5 px-1 outline-none transition-all uppercase placeholder:font-black placeholder:text-slate-300"
                    />
                  </td>

                  <td className="py-1.5 px-3">
                    <div className="relative">
                      <select 
                        value={row.returnReason}
                        onChange={(e) => updateRow(row.id, 'returnReason', e.target.value)}
                        className="w-full appearance-none bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-800 text-[9px] font-bold py-1.5 pl-2 pr-6 rounded-lg outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all cursor-pointer"
                      >
                        <option value="Defective">Defective</option>
                        <option value="Expired">Expired</option>
                        <option value="Wrong Item">Wrong Item</option>
                        <option value="Customer Choice">Customer Choice</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-slate-500">
                        <svg className="fill-current h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
                      </div>
                    </div>
                  </td>

                  <td className="py-1.5 px-3">
                    <input 
                      type="number" 
                      min="1"
                      value={row.qty}
                      onChange={(e) => updateRow(row.id, 'qty', e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 focus:border-rose-500 text-[9px] font-bold text-slate-800 py-1.5 px-2 rounded-lg outline-none transition-all shadow-sm text-center"
                    />
                  </td>

                  <td className="py-1.5 px-3">
                    <input 
                      type="number" 
                      min="0"
                      value={row.rate}
                      onChange={(e) => updateRow(row.id, 'rate', e.target.value)}
                      className="w-full bg-slate-50 border border-slate-200 focus:border-rose-500 text-[9px] font-bold text-slate-800 py-1.5 px-2 rounded-lg outline-none transition-all shadow-sm text-right"
                    />
                  </td>

                  <td className="py-1.5 px-3">
                    <div className="w-full bg-rose-50 text-[9px] font-black text-rose-600 py-1.5 px-3 rounded-lg border border-rose-100 flex items-center justify-end">
                      {row.amount.toFixed(2)}
                    </div>
                  </td>

                  <td className="py-1.5 px-3 text-center">
                    <button 
                      onClick={() => removeRow(row.id)}
                      className="text-slate-300 hover:text-rose-500 hover:bg-rose-50 p-1.5 rounded-lg transition-colors"
                      disabled={rows.length === 1}
                    >
                      <svg className="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Auto-add row handles adding new lines */}

        {/* Calculation Totals & Save */}
        <div className="p-0 border-t-2 border-slate-200 bg-white flex flex-col md:flex-row justify-between rounded-b-2xl overflow-hidden shrink-0 mt-auto">
          
          {/* Left Side: Amount in Words & Item Count */}
          <div className="p-2 flex flex-col justify-end flex-1 min-h-[120px]">
            <div className="text-[9px] font-bold text-slate-800 mb-2 border-b border-slate-200 pb-1 w-full text-right">
              Total Returned Items : {validRows.length}
            </div>
            <div className="text-[13px] font-bold text-rose-600 uppercase tracking-wide">
              Refund Rs.: {numberToWords(Math.round(netAmount))}
            </div>
          </div>

          {/* Right Side */}
          <div className="w-full md:w-80 border-t md:border-t-0 md:border-l border-slate-300 flex flex-col bg-white">
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200">
              <span className="text-[10px] sm:text-xs font-bold text-slate-800">Gross Return</span>
              <span className="text-[10px] sm:text-xs font-bold text-slate-800">{grossAmount.toFixed(2)}</span>
            </div>

            <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200">
              <span className="text-[10px] sm:text-xs font-bold text-slate-800">Less (Charge)</span>
              <input 
                type="number" 
                min="0"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                placeholder="0.00"
                className="w-24 text-right outline-none text-[10px] sm:text-xs font-bold text-slate-800 bg-transparent placeholder-slate-300"
              />
            </div>

            <div className="flex items-center justify-between px-4 py-1.5 border-b border-slate-200">
              <span className="text-[10px] sm:text-xs font-bold text-slate-800">Add (Tax/Fee)</span>
              <input 
                type="number" 
                min="0"
                value={addAmount}
                onChange={(e) => setAddAmount(e.target.value)}
                placeholder="0.00"
                className="w-24 text-right outline-none text-[10px] sm:text-xs font-bold text-slate-800 bg-transparent placeholder-slate-300"
              />
            </div>

            <div className="flex items-center justify-between px-5 py-2 bg-rose-600 text-white">
              <span className="text-sm sm:text-base font-bold tracking-wider">REFUND</span>
              <span className="text-lg sm:text-xl font-bold">{netAmount.toFixed(2)}</span>
            </div>

            <button 
              onClick={handleSaveReturn}
              disabled={isSaving}
              className={`w-full py-2.5 font-bold text-white text-xs sm:text-sm transition-all flex items-center justify-center gap-2 ${isSaving ? 'bg-slate-400 cursor-not-allowed' : 'bg-rose-700 hover:bg-rose-800'}`}
            >
              {isSaving ? 'Saving...' : 'Save Sales Return'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SalesReturn;
